import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChartLineUpIcon as ChartLineUp, CurrencyDollarIcon as CurrencyDollar, UsersIcon as Users, ArrowSquareOutIcon as ArrowSquareOut, CopySimpleIcon as CopySimple, CheckCircleIcon as CheckCircle, InfoIcon as Info, LightningIcon as Lightning, BellIcon as Bell, PlayCircleIcon as PlayCircle, DiscordLogoIcon as DiscordLogo, PaperPlaneTiltIcon as PaperPlaneTilt, RobotIcon as Robot, ChatTeardropDotsIcon as ChatTeardropDots, ClockIcon as Clock, CompassIcon as Compass, PushPinIcon as PushPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";

/**
 * /admin/analytics — PostHog dashboard hub.
 *
 *   1. Embed: paste a PostHog "Share dashboard publicly" URL, save,
 *      and the live dashboard renders inline as an iframe. No data
 *      ever leaves PostHog → Cloudflare → us; we just render the URL
 *      they pasted.
 *   2. Recipes: three opinionated starter dashboards (Acquisition
 *      funnel, Revenue pulse, 7-day retention) the admin can copy
 *      manually into PostHog.
 *   3. Event catalog: every event the platform emits, so admins can
 *      build their own charts without guessing event names.
 */
const Section = ({ icon: Icon, label, color, children, testId, action }) => (
  <section
    className="rounded-2xl border border-[#EAEAEA] bg-white shadow-luxury-light p-6 mb-5 relative"
    data-testid={testId}
  >
    <div className="flex items-start justify-between gap-3 mb-4">
      <div className="flex items-center gap-3">
        <span
          className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `${color}15`, color }}
        >
          <Icon size={20} weight="duotone" />
        </span>
        <h2 className="font-spectral text-2xl tracking-tight">{label}</h2>
      </div>
      {action}
    </div>
    <div className="gold-hairline mb-5" />
    {children}
  </section>
);

const RECIPE_ICONS = {
  acquisition_funnel: Users,
  revenue_pulse: CurrencyDollar,
  retention_cohort: Lightning,
};

export default function AdminAnalytics() {
  const [embedUrl, setEmbedUrl] = useState("");
  const [savedUrl, setSavedUrl] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);

  const [recipes, setRecipes] = useState([]);
  const [events, setEvents] = useState([]);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics/config")
      .then((r) => {
        setSavedUrl(r.data?.embed_url || "");
        setEmbedUrl(r.data?.embed_url || "");
        setSavedAt(r.data?.updated_at || null);
      })
      .catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/analytics/recipes")
      .then((r) => setRecipes(r.data?.recipes || []))
      .catch(() => {});
    api.get("/admin/analytics/events")
      .then((r) => setEvents(r.data?.events || []))
      .catch(() => {});
  }, []);

  const saveEmbed = async () => {
    setSaving(true);
    try {
      const trimmed = (embedUrl || "").trim();
      const { data } = await api.put("/admin/analytics/config", {
        embed_url: trimmed || null,
      });
      setSavedUrl(data.embed_url || "");
      setSavedAt(data.updated_at || null);
      toast.success(trimmed ? "Embed URL saved" : "Embed URL cleared");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const copyRecipe = (recipe) => {
    try {
      navigator.clipboard.writeText(JSON.stringify(recipe, null, 2));
      setCopiedId(recipe.id);
      toast.success("Recipe copied");
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <AppShell>
      <div className="mb-6" data-testid="admin-analytics-header">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">
          Admin · Analytics
        </p>
        <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          Product analytics
        </h1>
        <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
          PostHog is wired up and capturing events from every page. Embed your
          PostHog dashboard below to see live numbers right inside the admin
          console — or use the starter recipes to build them in PostHog first.
        </p>
      </div>

      {/* 1. Embed */}
      <Section
        icon={ChartLineUp}
        label="Embedded dashboard"
        color="#3F4A8A"
        testId="admin-analytics-embed"
        action={
          <Button
            asChild
            variant="outline"
            size="sm"
            className="rounded-full"
            data-testid="open-posthog-cloud"
          >
            <a
              href="https://us.i.posthog.com"
              target="_blank"
              rel="noreferrer"
            >
              Open PostHog <ArrowSquareOut size={14} className="ml-1.5" />
            </a>
          </Button>
        }
      >
        <div className="rounded-xl bg-[#FAFAFA] border border-[#EAEAEA] p-4 mb-4 flex items-start gap-3">
          <Info size={18} className="text-[#6B6B6B] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#3a3a3a] leading-relaxed">
            In PostHog → open the dashboard you want to embed →{" "}
            <strong>Share</strong> → toggle <em>Share publicly</em> → copy the
            URL it gives you (it looks like{" "}
            <code className="px-1 bg-white rounded">https://us.posthog.com/shared/…</code>
            ) and paste it below. We only accept <code>posthog.com</code> hosts.
          </div>
        </div>

        <div className="grid sm:grid-cols-[1fr,auto] gap-3 items-end">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B]">
              PostHog shared dashboard URL
            </Label>
            <Input
              value={embedUrl}
              onChange={(e) => setEmbedUrl(e.target.value)}
              placeholder="https://us.posthog.com/shared/…"
              data-testid="embed-url-input"
              className="mt-1.5"
            />
          </div>
          <Button
            onClick={saveEmbed}
            disabled={saving}
            className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full h-10 px-5"
            data-testid="embed-url-save"
          >
            {saving ? "Saving…" : savedUrl ? "Update" : "Save"}
          </Button>
        </div>
        {savedAt && (
          <p className="text-xs text-[#6B6B6B] mt-2">
            Last updated {new Date(savedAt).toLocaleString()}
          </p>
        )}

        {savedUrl ? (
          <div className="mt-5 rounded-xl overflow-hidden border border-[#EAEAEA] bg-[#0E0E0E]">
            <iframe
              key={savedUrl}
              src={savedUrl}
              title="PostHog dashboard"
              className="w-full"
              style={{ height: 720, border: 0 }}
              loading="lazy"
              data-testid="posthog-iframe"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        ) : (
          <div className="mt-5 rounded-xl border border-dashed border-[#D0D0D0] bg-[#FAFAFA] p-10 text-center">
            <ChartLineUp
              size={36}
              weight="duotone"
              className="mx-auto text-gold mb-3"
            />
            <p className="text-sm text-[#3a3a3a] max-w-md mx-auto">
              No dashboard embedded yet. Build one in PostHog using the recipes
              below, then paste the share URL above to see it live here.
            </p>
          </div>
        )}
      </Section>

      {/* 2. Recipes */}
      <Section
        icon={Lightning}
        label="Starter dashboard recipes"
        color="#C9A227"
        testId="admin-analytics-recipes"
      >
        <p className="text-sm text-[#6B6B6B] mb-5">
          Three opinionated dashboards we recommend you build first. Each card
          spells out the chart type, events, breakdowns, and time range so you
          can replicate it in PostHog in under five minutes.
        </p>

        <div className="grid md:grid-cols-3 gap-4">
          {recipes.map((r) => {
            const Icon = RECIPE_ICONS[r.id] || Lightning;
            return (
              <div
                key={r.id}
                className="rounded-xl border border-[#EAEAEA] bg-[#FAFAFA] p-5 flex flex-col"
                data-testid={`recipe-${r.id}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={18} weight="duotone" className="text-gold" />
                  <h3 className="font-spectral text-lg">{r.title}</h3>
                </div>
                <p className="text-xs font-medium text-[#6B6B6B] uppercase tracking-wide mb-3">
                  {r.subtitle}
                </p>
                <p className="text-sm text-[#3a3a3a] leading-relaxed mb-4 flex-1">
                  {r.why}
                </p>
                <dl className="text-xs space-y-1.5 mb-4">
                  <div className="flex gap-2">
                    <dt className="font-semibold text-[#6B6B6B] w-20 flex-shrink-0">
                      Chart
                    </dt>
                    <dd className="text-ink">{r.chart_type}</dd>
                  </div>
                  {r.steps && (
                    <div className="flex gap-2">
                      <dt className="font-semibold text-[#6B6B6B] w-20 flex-shrink-0">
                        Steps
                      </dt>
                      <dd className="text-ink">
                        {r.steps.map((s) => s.label).join(" → ")}
                      </dd>
                    </div>
                  )}
                  {r.events && (
                    <div className="flex gap-2">
                      <dt className="font-semibold text-[#6B6B6B] w-20 flex-shrink-0">
                        Events
                      </dt>
                      <dd className="text-ink">
                        {r.events.map((e) => e.event).join(", ")}
                      </dd>
                    </div>
                  )}
                  {r.first_event && (
                    <>
                      <div className="flex gap-2">
                        <dt className="font-semibold text-[#6B6B6B] w-20 flex-shrink-0">
                          Cohort
                        </dt>
                        <dd className="text-ink">{r.first_event}</dd>
                      </div>
                      <div className="flex gap-2">
                        <dt className="font-semibold text-[#6B6B6B] w-20 flex-shrink-0">
                          Returns
                        </dt>
                        <dd className="text-ink">{r.returning_event}</dd>
                      </div>
                    </>
                  )}
                  {r.breakdown_by && (
                    <div className="flex gap-2">
                      <dt className="font-semibold text-[#6B6B6B] w-20 flex-shrink-0">
                        Breakdown
                      </dt>
                      <dd className="text-ink">{r.breakdown_by}</dd>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <dt className="font-semibold text-[#6B6B6B] w-20 flex-shrink-0">
                      Range
                    </dt>
                    <dd className="text-ink">
                      {r.time_range || r.lookback || "Last 30 days"}
                    </dd>
                  </div>
                </dl>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyRecipe(r)}
                  className="rounded-full w-full"
                  data-testid={`recipe-copy-${r.id}`}
                >
                  {copiedId === r.id ? (
                    <>
                      <CheckCircle size={14} weight="bold" className="mr-1.5" />
                      Copied
                    </>
                  ) : (
                    <>
                      <CopySimple size={14} className="mr-1.5" />
                      Copy recipe JSON
                    </>
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      </Section>

      {/* 3. Event catalog */}
      <Section
        icon={Info}
        label="Event catalog"
        color="#0F8043"
        testId="admin-analytics-events"
      >
        <p className="text-sm text-[#6B6B6B] mb-4">
          Canonical list of every analytics event the platform emits. Use these
          exact names when building insights in PostHog.
        </p>
        <div className="overflow-x-auto rounded-xl border border-[#EAEAEA]">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[#6B6B6B] uppercase text-xs tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Event</th>
                <th className="text-left px-4 py-3 font-semibold">Source</th>
                <th className="text-left px-4 py-3 font-semibold">Trigger</th>
                <th className="text-left px-4 py-3 font-semibold">Properties</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EAEAEA]">
              {events.map((ev) => (
                <tr key={ev.name} data-testid={`event-row-${ev.name}`}>
                  <td className="px-4 py-3 font-mono text-xs text-crimson whitespace-nowrap">
                    {ev.name}
                  </td>
                  <td className="px-4 py-3 text-[#3a3a3a] whitespace-nowrap">
                    {ev.source}
                  </td>
                  <td className="px-4 py-3 text-[#3a3a3a]">{ev.trigger}</td>
                  <td className="px-4 py-3">
                    {Object.keys(ev.properties || {}).length === 0 ? (
                      <span className="text-[#9a9a9a]">—</span>
                    ) : (
                      <ul className="space-y-1">
                        {Object.entries(ev.properties).map(([k, v]) => (
                          <li key={k} className="text-xs">
                            <span className="font-mono text-[#3F4A8A]">{k}</span>
                            <span className="text-[#6B6B6B]"> · {v}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* 4. Anomaly alerts */}
      <AnomalySection />

      {/* 4b. Nav-click analytics — which Explore items each persona uses */}
      <NavClicksSection />

      {/* 5. Webhook outputs */}
      <WebhookSection />

      {/* 6. Discord two-way bot */}
      <DiscordBotSection />
    </AppShell>
  );
}


/**
 * AnomalySection — admin tile for the daily PostHog anomaly engine.
 * Surfaces:
 *   • A "Run now" button that kicks off a one-shot pass of the engine
 *     and returns the live summary (insufficient_data, alerted, etc.)
 *   • The dispatch audit trail — every alert ever fired, with the
 *     yesterday vs baseline values that triggered it.
 */
function AnomalySection() {
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [dispatches, setDispatches] = useState([]);
  const [loadingDispatches, setLoadingDispatches] = useState(true);

  const loadDispatches = () => {
    setLoadingDispatches(true);
    api.get("/admin/analytics/anomalies/dispatches")
      .then((r) => setDispatches(r.data?.dispatches || []))
      .catch(() => {})
      .finally(() => setLoadingDispatches(false));
  };

  useEffect(() => {
    loadDispatches();
  }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/analytics/anomalies/run-now");
      setLastRun(data);
      if (data.alerts_sent > 0) {
        toast.success(`Engine fired ${data.alerts_sent} alert${data.alerts_sent === 1 ? "" : "s"}`);
      } else if (data.skipped) {
        toast.error(data.reason || "Skipped");
      } else {
        toast.success("Engine ran — no anomalies detected");
      }
      loadDispatches();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Section
      icon={Bell}
      label="Anomaly alerting"
      color="#C1272D"
      testId="admin-analytics-anomalies"
      action={
        <Button
          onClick={runNow}
          disabled={running}
          size="sm"
          className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full h-9 px-4"
          data-testid="anomaly-run-now"
        >
          <PlayCircle size={14} weight="fill" className="mr-1.5" />
          {running ? "Running…" : "Run now"}
        </Button>
      }
    >
      <p className="text-sm text-[#6B6B6B] mb-4 max-w-2xl">
        A daily background job compares yesterday's signups, paid sessions, and
        gross revenue against the 14-day rolling median. If a metric drops more
        than 50% or spikes more than 2× the typical baseline, every admin gets
        a message in their inbox. Runs once per day, idempotent (won't
        double-DM you).
      </p>

      {lastRun && (
        <div className="rounded-xl border border-[#EAEAEA] bg-[#FAFAFA] p-4 mb-5" data-testid="anomaly-last-run">
          <p className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-2">
            Last run · {new Date(lastRun.ran_at || Date.now()).toLocaleString()}
          </p>
          <div className="space-y-1.5">
            {(lastRun.checks || []).map((c) => (
              <div key={c.metric} className="text-sm flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-ink">{c.metric}</span>
                <span className="text-[#6B6B6B]">&rarr;</span>
                <span className={
                  c.direction === "drop"
                    ? "text-[#C1272D] font-semibold"
                    : c.direction === "spike"
                    ? "text-[#8A6A00] font-semibold"
                    : "text-[#3a3a3a]"
                }>
                  {c.status || c.direction || "ok"}
                </span>
                {c.yesterday !== undefined && c.baseline_median_14d !== undefined && (
                  <span className="text-xs text-[#6B6B6B]">
                    (yesterday {c.yesterday} vs 14d median {c.baseline_median_14d})
                  </span>
                )}
              </div>
            ))}
          </div>
          {lastRun.skipped && (
            <p className="text-sm text-crimson mt-2">Skipped: {lastRun.reason}</p>
          )}
        </div>
      )}

      <h3 className="font-spectral text-lg mb-3">Dispatch history</h3>
      {loadingDispatches ? (
        <p className="text-sm text-[#6B6B6B]">Loading&hellip;</p>
      ) : dispatches.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#D0D0D0] bg-[#FAFAFA] p-6 text-center text-sm text-[#6B6B6B]" data-testid="anomaly-dispatches-empty">
          No anomaly alerts have been fired yet. The engine needs at least 8
          days of baseline data before it will alert on anything.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[#EAEAEA]">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[#6B6B6B] uppercase text-xs tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Date</th>
                <th className="text-left px-4 py-3 font-semibold">Metric</th>
                <th className="text-left px-4 py-3 font-semibold">Direction</th>
                <th className="text-right px-4 py-3 font-semibold">Yesterday</th>
                <th className="text-right px-4 py-3 font-semibold">Baseline</th>
                <th className="text-right px-4 py-3 font-semibold">Recipients</th>
                <th className="text-left px-4 py-3 font-semibold">Sent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EAEAEA]">
              {dispatches.map((d) => (
                <tr key={d.id} data-testid={`anomaly-dispatch-${d.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap">{d.date}</td>
                  <td className="px-4 py-3 font-mono text-xs">{d.metric}</td>
                  <td className="px-4 py-3">
                    <span className={
                      d.direction === "drop"
                        ? "inline-flex items-center gap-1 text-[#C1272D] font-semibold"
                        : "inline-flex items-center gap-1 text-[#8A6A00] font-semibold"
                    }>
                      {d.direction}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {d.yesterday}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {d.baseline_median_14d?.toFixed
                      ? d.baseline_median_14d.toFixed(1)
                      : d.baseline_median_14d}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {d.recipients}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#6B6B6B] whitespace-nowrap">
                    {new Date(d.sent_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

/**
 * WebhookSection — paste a Discord webhook URL, save, test, clear.
 * The saved URL is redacted server-side in GET responses so the UI
 * only ever sees a preview like `https://discord.com/api/webhooks/<id>/********w8UM`.
 */
function WebhookSection() {
  const [config, setConfig] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = () => {
    api.get("/admin/analytics/webhook")
      .then((r) => setConfig(r.data))
      .catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const url = (draft || "").trim();
      const { data } = await api.put("/admin/analytics/webhook", { url: url || null });
      setConfig(data);
      setDraft("");
      toast.success(url ? "Webhook saved" : "Webhook cleared");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      await api.post("/admin/analytics/webhook/test");
      toast.success("Test ping sent — check your Discord channel");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  return (
    <Section
      icon={DiscordLogo}
      label="Webhook outputs"
      color="#5865F2"
      testId="admin-analytics-webhook"
    >
      <p className="text-sm text-[#6B6B6B] mb-4 max-w-2xl">
        Forward anomaly alerts to a Discord channel so they hit your phone
        anywhere. Paste the webhook URL below — we'll only ever store + show
        a redacted preview after saving.
      </p>

      <div className="rounded-xl bg-[#FAFAFA] border border-[#EAEAEA] p-4 mb-4 flex items-start gap-3">
        <Info size={18} className="text-[#6B6B6B] flex-shrink-0 mt-0.5" />
        <div className="text-sm text-[#3a3a3a] leading-relaxed">
          In Discord → channel settings (gear icon) → <strong>Integrations</strong> →
          <strong> Webhooks</strong> → <strong>New Webhook</strong> → name it anything
          (e.g. "Armstrong K9 Bot") → <strong>Copy Webhook URL</strong>. Paste it
          here, hit Save, then click <em>Send test message</em> to confirm wiring.
        </div>
      </div>

      {config?.enabled && config?.url_preview && (
        <div
          className="rounded-xl border border-[#0F8043]/30 bg-[#0F8043]/5 p-4 mb-4 flex items-center justify-between gap-3 flex-wrap"
          data-testid="webhook-current"
        >
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0F8043] mb-1">
              Active
            </p>
            <code className="text-xs text-[#3a3a3a] break-all">{config.url_preview}</code>
            {config.updated_at && (
              <p className="text-xs text-[#6B6B6B] mt-1">
                Saved {new Date(config.updated_at).toLocaleString()}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={test}
              disabled={testing}
              className="rounded-full"
              data-testid="webhook-test"
            >
              <PaperPlaneTilt size={14} className="mr-1.5" />
              {testing ? "Sending…" : "Send test message"}
            </Button>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-[1fr,auto] gap-3 items-end">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B]">
            {config?.enabled ? "Replace webhook URL" : "Discord webhook URL"}
          </Label>
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="https://discord.com/api/webhooks/…"
            data-testid="webhook-url-input"
            className="mt-1.5 font-mono text-xs"
          />
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#0E0E0E] hover:bg-[#5865F2] text-white rounded-full h-10 px-5"
          data-testid="webhook-save"
        >
          {saving ? "Saving…" : config?.enabled ? "Replace" : "Save"}
        </Button>
      </div>

      {config?.enabled && (
        <button
          type="button"
          onClick={() => { setDraft(""); api.put("/admin/analytics/webhook", { url: null }).then(load).then(() => toast.success("Webhook cleared")); }}
          className="text-xs text-crimson hover:underline mt-3 inline-block"
          data-testid="webhook-clear"
        >
          Disconnect webhook
        </button>
      )}
    </Section>
  );
}


/**
 * DiscordBotSection — wires up the two-way Discord bot.
 *
 * Three checklist items the admin walks through (top → bottom):
 *   1. Application ID + Public Key set in env (turns green automatically
 *      once the agent restarts with them)
 *   2. Bot token set in env → click "Register slash commands"
 *   3. Paste the Interactions Endpoint URL into Discord Developer Portal
 *      (we show the URL with a copy button)
 *
 * Active silences are rendered live with a "until" timestamp so the
 * admin can confirm a /silence command landed correctly.
 */
function DiscordBotSection() {
  const [status, setStatus] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = () => {
    api.get("/admin/analytics/discord/status")
      .then((r) => setStatus(r.data))
      .catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const registerCommands = async () => {
    setRegistering(true);
    try {
      const { data } = await api.post("/admin/analytics/discord/register-commands");
      const where = data.scope === "guild"
        ? `your guild (${data.guild_id})`
        : "globally (may take up to 1 hour to appear)";
      toast.success(`Registered ${data.registered.join(", ")} ${where}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRegistering(false);
    }
  };

  const copyUrl = () => {
    if (!status?.interactions_url) return;
    try {
      navigator.clipboard.writeText(status.interactions_url);
      setCopied(true);
      toast.success("Interactions URL copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy");
    }
  };

  const Step = ({ done, n, label, children }) => (
    <div className="flex gap-3 items-start">
      <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${done
        ? "bg-[#0F8043] text-white"
        : "bg-[#FAFAFA] border border-[#D0D0D0] text-[#6B6B6B]"}`}>
        {done ? <CheckCircle size={14} weight="bold" /> : n}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold ${done ? "text-[#0F8043]" : "text-[#0E0E0E]"}`}>
          {label}
        </p>
        <div className="text-sm text-[#3a3a3a] mt-1 leading-relaxed">{children}</div>
      </div>
    </div>
  );

  const allReady = status?.application_id_set && status?.public_key_set && status?.bot_token_set;

  return (
    <Section
      icon={Robot}
      label="Discord two-way bot"
      color="#5865F2"
      testId="admin-analytics-discord-bot"
    >
      <p className="text-sm text-[#6B6B6B] mb-5 max-w-2xl">
        Reply to alerts straight from Discord. Type <code>/ack</code> to mark
        an anomaly triaged, or <code>/silence hours metric</code> to pause a
        noisy metric for up to a week. The anomaly engine respects active
        silences automatically.
      </p>

      <div className="space-y-4 mb-6" data-testid="discord-bot-checklist">
        <Step
          done={status?.application_id_set && status?.public_key_set}
          n={1}
          label="Application ID + Public Key configured"
        >
          {status?.application_id_set && status?.public_key_set ? (
            <span className="text-[#6B6B6B] text-xs">
              Set in <code>backend/.env</code> · changes require a restart.
            </span>
          ) : (
            <span className="text-crimson">
              Add <code>DISCORD_APPLICATION_ID</code> and{" "}
              <code>DISCORD_PUBLIC_KEY</code> to <code>backend/.env</code>,
              then restart the backend.
            </span>
          )}
        </Step>

        <Step done={status?.bot_token_set} n={2} label="Bot token configured">
          {status?.bot_token_set ? (
            <Button
              size="sm"
              variant="outline"
              className="rounded-full mt-2"
              onClick={registerCommands}
              disabled={registering}
              data-testid="discord-register-commands"
            >
              <ChatTeardropDots size={14} className="mr-1.5" />
              {registering ? "Registering…" : "Register /ack + /silence with Discord"}
            </Button>
          ) : (
            <span className="text-crimson">
              Add <code>DISCORD_BOT_TOKEN</code> to <code>backend/.env</code>,
              restart, then come back to register the slash commands.
            </span>
          )}
        </Step>

        <Step done={false} n={3} label="Paste the Interactions Endpoint URL into Discord">
          <p className="mb-2">
            In Discord Developer Portal → your application → <strong>General Information</strong> →
            scroll to <strong>Interactions Endpoint URL</strong> → paste the URL below → Save.
            Discord will ping the URL once to verify, then route every <code>/ack</code> and{" "}
            <code>/silence</code> here.
          </p>
          <div className="flex items-center gap-2 rounded-lg border border-[#EAEAEA] bg-[#FAFAFA] p-2">
            <code className="text-xs text-[#3a3a3a] break-all flex-1" data-testid="interactions-url">
              {status?.interactions_url || "Set APP_PUBLIC_URL after deploy to see this"}
            </code>
            {status?.interactions_url && (
              <Button
                size="sm"
                variant="ghost"
                onClick={copyUrl}
                className="h-7 px-2 flex-shrink-0"
                data-testid="copy-interactions-url"
              >
                {copied ? (
                  <CheckCircle size={14} weight="bold" className="text-[#0F8043]" />
                ) : (
                  <CopySimple size={14} />
                )}
              </Button>
            )}
          </div>
        </Step>
      </div>

      {allReady && (
        <div className="rounded-xl border border-[#0F8043]/30 bg-[#0F8043]/5 p-4 mb-5 flex items-start gap-3">
          <CheckCircle size={20} weight="duotone" className="text-[#0F8043] flex-shrink-0" />
          <div className="text-sm text-[#0F8043]">
            <strong>Bot is wired.</strong> Try{" "}
            <code className="bg-white px-1.5 py-0.5 rounded">
              /silence hours:1 metric:signups
            </code>{" "}
            in your Discord server to confirm. The silence will appear below.
          </div>
        </div>
      )}

      <h3 className="font-spectral text-lg mb-3">Active silences</h3>
      {status?.active_silences?.length ? (
        <div className="overflow-x-auto rounded-xl border border-[#EAEAEA]">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[#6B6B6B] uppercase text-xs tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Metric</th>
                <th className="text-left px-4 py-3 font-semibold">Until</th>
                <th className="text-left px-4 py-3 font-semibold">Set by Discord ID</th>
                <th className="text-right px-4 py-3 font-semibold">Hours</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EAEAEA]">
              {status.active_silences.map((s) => (
                <tr key={s.id} data-testid={`silence-row-${s.metric}`}>
                  <td className="px-4 py-3 font-mono text-xs">{s.metric}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <Clock size={12} weight="bold" className="inline mr-1 text-[#6B6B6B]" />
                    {new Date(s.expires_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#6B6B6B] font-mono">
                    {s.set_by_discord}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{s.hours}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          className="rounded-xl border border-dashed border-[#D0D0D0] bg-[#FAFAFA] p-6 text-center text-sm text-[#6B6B6B]"
          data-testid="silences-empty"
        >
          No active silences. Run <code>/silence hours:1 metric:all</code> in
          Discord to add one.
        </div>
      )}
    </Section>
  );
}




/**
 * NavClicksSection — surfaces "Top Explore clicks last N days by persona."
 *
 * Reads from `GET /api/admin/analytics/nav-clicks?days=7`. Backed by the
 * `nav_click_events` collection populated when any user clicks an item
 * in the desktop Explore dropdown.
 */
function NavClicksSection() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/admin/analytics/nav-clicks?days=${days}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(formatApiError(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const personaOrder = ["breeder", "buyer", "dog_owner", "transporter", "service_provider", "storefront_seller"];
  const personaLabel = {
    breeder: "Breeders",
    buyer: "Buyers",
    dog_owner: "Dog owners",
    transporter: "Transporters",
    service_provider: "Service pros",
    storefront_seller: "Storefront sellers",
  };

  return (
    <Section
      icon={Compass}
      label="Nav-click analytics"
      color="#C9A227"
      testId="admin-nav-clicks-section"
      action={
        <div className="flex items-center gap-1 text-xs">
          {[1, 7, 30].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`px-2.5 py-1 rounded-full border transition-colors ${
                days === d
                  ? "bg-ink text-white border-ink"
                  : "bg-white text-[#6B6B6B] border-[#E5E5E5] hover:border-ink"
              }`}
              data-testid={`nav-clicks-range-${d}d`}
            >
              {d === 1 ? "24h" : `${d}d`}
            </button>
          ))}
        </div>
      }
    >
      <p className="text-sm text-[#6B6B6B] mb-4 max-w-2xl">
        Which Explore-dropdown items each persona actually clicks. Admin clicks
        are excluded so the data reflects real user behaviour. Use this to
        decide which items to promote to a top-level pill — or which to retire
        from the dropdown entirely.
      </p>

      {loading && (
        <div className="text-[#6B6B6B] text-sm py-6" data-testid="nav-clicks-loading">
          Loading nav-click data…
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-[#FAD7DA] bg-[#FFF5F5] text-[#9B1C24] text-sm px-3 py-2 mb-3" data-testid="nav-clicks-error">
          {error}
        </div>
      )}

      {data && !loading && (
        <>
          <div className="flex flex-wrap gap-3 mb-5 text-xs">
            <span className="rounded-full bg-cream px-3 py-1 border border-[#E2DAC2]" data-testid="nav-clicks-total">
              <b className="text-ink">{data.total_clicks.toLocaleString()}</b> clicks
            </span>
            <span className="rounded-full bg-cream px-3 py-1 border border-[#E2DAC2]" data-testid="nav-clicks-users">
              <b className="text-ink">{data.unique_users.toLocaleString()}</b> users
            </span>
            <span className="text-[#9A9A9A] py-1">last {data.days}d</span>
          </div>

          {data.total_clicks === 0 ? (
            <div className="text-sm text-[#6B6B6B] bg-[#FAFAFA] rounded-lg p-4 border border-dashed border-[#E5E5E5]">
              No nav-clicks captured in this window yet. The event fires on
              every Explore-dropdown click, so this will start filling in as
              users browse the app. Admin clicks are excluded.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div data-testid="nav-clicks-overall">
                <h3 className="text-[10px] uppercase tracking-[0.22em] text-[#6B6B6B] font-bold mb-2">
                  Top destinations (overall)
                </h3>
                <ol className="space-y-1.5">
                  {data.totals_by_destination.slice(0, 10).map((row, i) => (
                    <li key={row.destination} className="flex items-center gap-3 text-sm">
                      <span className="text-[10px] font-bold text-[#9A9A9A] w-5 text-right">{i + 1}</span>
                      <span className="flex-1 min-w-0 truncate text-ink" title={row.destination}>{row.label || row.destination}</span>
                      <span className="rounded-full bg-ink text-white text-[10px] font-semibold px-2 py-0.5">
                        {row.count}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>

              <div data-testid="nav-clicks-by-persona">
                <h3 className="text-[10px] uppercase tracking-[0.22em] text-[#6B6B6B] font-bold mb-2">
                  Top by persona
                </h3>
                <div className="space-y-3">
                  {personaOrder
                    .filter((p) => data.by_persona[p]?.length)
                    .map((persona) => (
                      <div key={persona} className="rounded-lg border border-[#EAEAEA] bg-[#FAFAFA] p-2.5">
                        <div className="flex items-center justify-between text-[11px] text-[#6B6B6B] font-semibold mb-1.5">
                          <span>{personaLabel[persona] || persona}</span>
                          <span>{data.by_persona[persona].reduce((sum, r) => sum + r.count, 0)} clicks</span>
                        </div>
                        <ol className="space-y-1">
                          {data.by_persona[persona].slice(0, 5).map((row) => (
                            <li key={row.destination} className="flex items-center gap-2 text-[12px]">
                              <span className="flex-1 truncate text-ink" title={row.destination}>{row.label || row.destination}</span>
                              <span className="text-[10px] font-semibold text-[#6B6B6B]">{row.count}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    ))}
                  {personaOrder.every((p) => !data.by_persona[p]?.length) && (
                    <div className="text-xs text-[#9A9A9A]">No persona-tagged clicks yet.</div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="mt-5 text-[11px] text-[#9A9A9A] flex items-center gap-1.5">
            <PushPin size={11} weight="fill" className="text-gold-dark" />
            Tip: items with high cross-persona traffic are good candidates for promotion to a top-level nav pill.
          </div>
        </>
      )}
    </Section>
  );
}
